import { hashString, seededRandom } from '@/lib/public/effects/seed';

/**
 * Topo: contour lines over a terrain grown from the event name, so every
 * event gets its own hills and the same event always gets the same ones.
 *
 * The terrain is value noise on a 64 x 64 lattice filled from the seed,
 * three octaves deep; the contours are marching squares over it. Pure, so
 * it is tested without a browser; the canvas only strokes the segments.
 */

const N = 64;

/** The terrain height (0 to 1) at any point, for one seed. */
export function topoField(seed: string): (x: number, y: number) => number {
  const r = seededRandom(hashString(`topo:${seed}`));
  const lat = new Float32Array(N * N);
  for (let i = 0; i < lat.length; i++) lat[i] = r();
  const L = (a: number, b: number) => lat[(b & (N - 1)) * N + (a & (N - 1))];
  const vn = (x: number, y: number) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    let fx = x - xi;
    let fy = y - yi;
    fx = fx * fx * (3 - 2 * fx);
    fy = fy * fy * (3 - 2 * fy);
    const a = L(xi, yi);
    const b = L(xi + 1, yi);
    const c = L(xi, yi + 1);
    const d = L(xi + 1, yi + 1);
    return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
  };
  return (x, y) => (vn(x, y) + 0.5 * vn(x * 2 + 11, y * 2 + 7) + 0.25 * vn(x * 4 + 3, y * 4 + 19)) / 1.75;
}

export interface TopoContours {
  /** Every fourth level, drawn heavier: x1, y1, x2, y2 per segment, in CSS pixels. */
  major: Float32Array;
  minor: Float32Array;
}

export interface TopoOptions {
  /** Grid step in pixels (smaller is smoother and slower). */
  step?: number;
  /** Pixels per noise unit: bigger hills when larger. */
  scale?: number;
}

const LEVEL_FROM = 0.24;
const LEVEL_TO = 0.8;
const LEVEL_GAP = 0.04;

/** The contour segments for a w x h box. */
export function topoContours(seed: string, w: number, h: number, opts: TopoOptions = {}): TopoContours {
  const step = Math.max(2, opts.step ?? 6);
  const scale = Math.max(10, opts.scale ?? 150);
  const field = topoField(seed);
  const cols = Math.ceil(w / step) + 1;
  const rows = Math.ceil(h / step) + 1;
  const F = new Float32Array(cols * rows);
  for (let j = 0; j < rows; j++) for (let k = 0; k < cols; k++) F[j * cols + k] = field((k * step) / scale, (j * step) / scale);

  const major: number[] = [];
  const minor: number[] = [];
  let level = 0;
  for (let t = LEVEL_FROM; t < LEVEL_TO; t += LEVEL_GAP, level++) {
    const out = level % 4 === 0 ? major : minor;
    for (let y = 0; y < rows - 1; y++) {
      for (let x = 0; x < cols - 1; x++) {
        const A = F[y * cols + x];
        const B = F[y * cols + x + 1];
        const C = F[(y + 1) * cols + x + 1];
        const D = F[(y + 1) * cols + x];
        const idx = (A > t ? 8 : 0) | (B > t ? 4 : 0) | (C > t ? 2 : 0) | (D > t ? 1 : 0);
        if (idx === 0 || idx === 15) continue;
        const x0 = x * step;
        const y0 = y * step;
        const T = [x0 + (step * (t - A)) / (B - A), y0];
        const R = [x0 + step, y0 + (step * (t - B)) / (C - B)];
        const Bo = [x0 + (step * (t - D)) / (C - D), y0 + step];
        const Le = [x0, y0 + (step * (t - A)) / (D - A)];
        const segs = SEGMENTS[idx](T, R, Bo, Le);
        for (let s = 0; s < segs.length; s += 2) out.push(segs[s][0], segs[s][1], segs[s + 1][0], segs[s + 1][1]);
      }
    }
  }
  return { major: Float32Array.from(major), minor: Float32Array.from(minor) };
}

type P = number[];
const SEGMENTS: Record<number, (T: P, R: P, Bo: P, Le: P) => P[]> = {
  1: (T, R, Bo, Le) => [Le, Bo],
  2: (T, R, Bo) => [Bo, R],
  3: (T, R, Bo, Le) => [Le, R],
  4: (T, R) => [T, R],
  5: (T, R, Bo, Le) => [Le, T, Bo, R],
  6: (T, R, Bo) => [T, Bo],
  7: (T, R, Bo, Le) => [Le, T],
  8: (T, R, Bo, Le) => [Le, T],
  9: (T, R, Bo) => [T, Bo],
  10: (T, R, Bo, Le) => [T, R, Le, Bo],
  11: (T, R) => [T, R],
  12: (T, R, Bo, Le) => [Le, R],
  13: (T, R, Bo) => [Bo, R],
  14: (T, R, Bo, Le) => [Le, Bo],
};

/** A few recent drawings, so a remount or a second tile of the same size doesn't redo the maths. */
const cache = new Map<string, TopoContours>();
export function cachedTopoContours(seed: string, w: number, h: number, opts: TopoOptions = {}): TopoContours {
  const key = `${seed}|${Math.round(w)}|${Math.round(h)}|${opts.step ?? 6}|${opts.scale ?? 150}`;
  let hit = cache.get(key);
  if (!hit) {
    hit = topoContours(seed, w, h, opts);
    cache.set(key, hit);
    if (cache.size > 24) cache.delete(cache.keys().next().value as string);
  }
  return hit;
}
